import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, TrendingUp, Target, Calendar, Mail, Download,
  Trash2, ChevronRight, ArrowLeft, BarChart3, Users, Check, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'per_session', label: 'Per Session' },
];

const TRACKING_TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage Accuracy', description: 'Track by % correct (0-100%)' },
  { value: 'steps_correct', label: 'Steps/Problems Correct', description: 'Track X correct out of Y total' },
];

const SCORE_OPTIONS = [
  { value: 1, label: '1 - Needs Support', color: '#EF4444' },
  { value: 2, label: '2 - Developing', color: '#F97316' },
  { value: 3, label: '3 - Progressing', color: '#EAB308' },
  { value: 4, label: '4 - Proficient', color: '#22C55E' },
  { value: 5, label: '5 - Mastered', color: '#5D8D78' },
];

const ProgressTracking = ({ user }) => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  
  const [folders, setFolders] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(studentId || '');
  const [goals, setGoals] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [addGoalOpen, setAddGoalOpen] = useState(false);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [emailReportOpen, setEmailReportOpen] = useState(false);
  const [deleteGoalOpen, setDeleteGoalOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  
  // Form states
  const [newGoal, setNewGoal] = useState({ name: '', description: '', target_frequency: 'daily', tracking_type: 'percentage' });
  const [newEntry, setNewEntry] = useState({ goal_id: '', date: new Date().toISOString().split('T')[0], score: 0, total_possible: 10, notes: '' });
  const [emailForm, setEmailForm] = useState({ recipient_email: '', recipient_name: '', custom_message: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchGoalsAndReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`, { withCredentials: true });
      setFolders(response.data);
      if (studentId && response.data.find(f => f.folder_id === studentId)) {
        setSelectedStudent(studentId);
      } else if (response.data.length > 0 && !studentId) {
        // Auto-select first student
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoalsAndReport = async () => {
    try {
      const [goalsRes, reportRes] = await Promise.all([
        axios.get(`${API}/progress/goals/student/${selectedStudent}`, { withCredentials: true }),
        axios.get(`${API}/progress/report/${selectedStudent}?days=30`, { withCredentials: true })
      ]);
      setGoals(goalsRes.data);
      setReport(reportRes.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.name.trim() || !selectedStudent) {
      toast.error('Please enter a goal name');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/progress/goals`, {
        student_id: selectedStudent,
        name: newGoal.name.trim(),
        description: newGoal.description.trim() || null,
        target_frequency: newGoal.target_frequency,
        tracking_type: newGoal.tracking_type
      }, { withCredentials: true });
      
      setAddGoalOpen(false);
      setNewGoal({ name: '', description: '', target_frequency: 'daily', tracking_type: 'percentage' });
      fetchGoalsAndReport();
      toast.success('Goal created!');
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async () => {
    if (!goalToDelete) return;
    
    try {
      await axios.delete(`${API}/progress/goals/${goalToDelete.goal_id}`, { withCredentials: true });
      setDeleteGoalOpen(false);
      setGoalToDelete(null);
      fetchGoalsAndReport();
      toast.success('Goal deleted');
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const handleSaveEntry = async () => {
    if (!newEntry.goal_id) {
      toast.error('Please select a goal');
      return;
    }
    
    // Get the selected goal's tracking type
    const selectedGoal = goals.find(g => g.goal_id === newEntry.goal_id);
    const trackingType = selectedGoal?.tracking_type || 'percentage';
    
    // Validate based on tracking type
    if (trackingType === 'steps_correct' && (!newEntry.total_possible || newEntry.total_possible < 1)) {
      toast.error('Please enter the total number of steps/problems');
      return;
    }
    
    if (trackingType === 'steps_correct' && newEntry.score > newEntry.total_possible) {
      toast.error('Score cannot exceed total possible');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/progress/entries`, {
        goal_id: newEntry.goal_id,
        student_id: selectedStudent,
        date: newEntry.date,
        score: newEntry.score,
        total_possible: trackingType === 'steps_correct' ? newEntry.total_possible : null,
        notes: newEntry.notes.trim() || null
      }, { withCredentials: true });
      
      setAddEntryOpen(false);
      setNewEntry({ goal_id: '', date: new Date().toISOString().split('T')[0], score: 0, total_possible: 10, notes: '' });
      fetchGoalsAndReport();
      toast.success('Progress recorded!');
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailReport = async () => {
    if (!emailForm.recipient_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/progress/email-report/${selectedStudent}`, {
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name || null,
        custom_message: emailForm.custom_message || null
      }, { withCredentials: true });
      
      setEmailReportOpen(false);
      setEmailForm({ recipient_email: '', recipient_name: '', custom_message: '' });
      toast.success('Progress report sent!');
    } catch (error) {
      console.error('Error emailing report:', error);
      toast.error('Failed to send email');
    } finally {
      setSaving(false);
    }
  };

  const downloadReportPdf = () => {
    if (!report) return;
    
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    
    // Background
    pdf.setFillColor(253, 251, 247);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(24);
    pdf.setTextColor(93, 141, 120);
    pdf.text('Progress Report', pageWidth / 2, 25, { align: 'center' });
    
    pdf.setFontSize(16);
    pdf.setTextColor(45, 52, 54);
    pdf.text(report.student_name, pageWidth / 2, 35, { align: 'center' });
    
    pdf.setFontSize(10);
    pdf.setTextColor(138, 145, 146);
    pdf.text(report.report_period, pageWidth / 2, 43, { align: 'center' });
    
    // Summary box
    pdf.setFillColor(93, 141, 120);
    pdf.roundedRect(margin, 50, pageWidth - 2 * margin, 20, 3, 3, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text(`Goals: ${report.total_goals}  |  Total Entries: ${report.total_entries}`, pageWidth / 2, 62, { align: 'center' });
    
    // Goals
    let yPos = 85;
    Object.values(report.goal_stats || {}).forEach((stats) => {
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 30;
      }
      
      // Goal box
      pdf.setFillColor(245, 242, 234);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 45, 3, 3, 'F');
      
      // Goal name
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(45, 52, 54);
      pdf.text(stats.goal_name, margin + 10, yPos + 12);
      
      // Stats
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(86, 93, 94);
      pdf.text(`Average Score: ${stats.average_score}/5`, margin + 10, yPos + 24);
      pdf.text(`Total Entries: ${stats.total_entries}`, margin + 10, yPos + 32);
      
      // Trend
      const trendColor = stats.trend === 'improving' ? [93, 141, 120] : [244, 162, 97];
      pdf.setTextColor(...trendColor);
      const trendText = stats.trend === 'improving' ? '↑ Improving' : stats.trend === 'stable' ? '→ Stable' : 'No data';
      pdf.text(trendText, margin + 10, yPos + 40);
      
      // Mini chart
      const entries = stats.entries_by_date.slice(-10);
      if (entries.length > 0) {
        const chartX = pageWidth - margin - 60;
        const chartWidth = 50;
        const barWidth = chartWidth / entries.length - 2;
        
        entries.forEach((entry, i) => {
          const height = entry.score * 6;
          pdf.setFillColor(93, 141, 120);
          pdf.rect(chartX + i * (barWidth + 2), yPos + 38 - height, barWidth, height, 'F');
        });
      }
      
      yPos += 55;
    });
    
    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(138, 145, 146);
    pdf.text('Generated by SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`Progress_Report_${report.student_name.replace(/\s+/g, '_')}.pdf`);
    toast.success('Report downloaded!');
  };

  const getScoreColor = (score) => {
    return SCORE_OPTIONS.find(s => s.value === score)?.color || '#8A9192';
  };

  const selectedFolder = folders.find(f => f.folder_id === selectedStudent);

  return (
    <div className="min-h-screen bg-background" data-testid="progress-tracking-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/students')}
              variant="ghost"
              className="text-text-secondary hover:text-primary"
            >
              <Users className="w-4 h-4 mr-2" />
              Students
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-text-primary mb-1">
              <BarChart3 className="w-8 h-8 inline mr-2 text-primary" />
              Progress Tracking
            </h1>
            <p className="font-body text-text-secondary">
              Track student skills and generate progress reports
            </p>
          </div>
        </div>

        {/* Student Selector */}
        <div className="bg-white rounded-2xl p-6 shadow-card mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <Label className="font-body font-semibold text-text-secondary mb-2 block">
                Select Student
              </Label>
              <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                <SelectTrigger className="w-full sm:w-64 border-2 border-primary/20 rounded-xl" data-testid="student-selector">
                  <SelectValue placeholder="Choose a student" />
                </SelectTrigger>
                <SelectContent>
                  {folders.map((folder) => (
                    <SelectItem key={folder.folder_id} value={folder.folder_id}>
                      {folder.student_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedStudent && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setAddGoalOpen(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full"
                  data-testid="add-goal-btn"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Add Goal
                </Button>
                <Button
                  onClick={() => setAddEntryOpen(true)}
                  disabled={goals.length === 0}
                  className="bg-accent text-white hover:bg-accent/90 rounded-full"
                  data-testid="record-progress-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Progress
                </Button>
              </div>
            )}
          </div>
        </div>

        {!selectedStudent ? (
          <div className="bg-white rounded-2xl p-12 shadow-card text-center">
            <div className="w-20 h-20 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
              Select a Student
            </h3>
            <p className="font-body text-text-secondary mb-4">
              Choose a student to view and track their progress
            </p>
            {folders.length === 0 && (
              <Button
                onClick={() => navigate('/students')}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
              >
                Create Student Folders First
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Report Summary */}
            {report && (
              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-6 text-white mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="font-heading font-bold text-2xl mb-1">{report.student_name}</h2>
                    <p className="text-white/80">{report.report_period}</p>
                    <div className="flex gap-6 mt-4">
                      <div>
                        <p className="text-white/60 text-sm">Goals</p>
                        <p className="font-heading font-bold text-2xl">{report.total_goals}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm">Total Entries</p>
                        <p className="font-heading font-bold text-2xl">{report.total_entries}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={downloadReportPdf}
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/20 rounded-full"
                      data-testid="download-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      onClick={() => setEmailReportOpen(true)}
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/20 rounded-full"
                      data-testid="email-report-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Goals Grid */}
            <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Skill Goals</h2>
            
            {goals.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 shadow-card text-center">
                <Target className="w-12 h-12 text-primary/30 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  No goals yet
                </h3>
                <p className="font-body text-text-secondary mb-4">
                  Create skill goals to start tracking progress
                </p>
                <Button
                  onClick={() => setAddGoalOpen(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full"
                >
                  Add First Goal
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {goals.map((goal) => {
                  const stats = report?.goal_stats?.[goal.goal_id] || {};
                  const entries = stats.entries_by_date || [];
                  const trackingType = goal.tracking_type || 'percentage';
                  
                  // Helper to calculate bar height based on tracking type
                  const getBarHeight = (entry) => {
                    if (trackingType === 'percentage') {
                      return `${Math.max(4, entry.score * 0.8)}px`; // Max 80px for 100%
                    } else if (trackingType === 'steps_correct' && entry.total_possible) {
                      const percentage = (entry.score / entry.total_possible) * 100;
                      return `${Math.max(4, percentage * 0.8)}px`;
                    }
                    return `${Math.max(4, entry.score * 16)}px`; // Fallback for old scale data
                  };
                  
                  // Helper to get bar color based on tracking type
                  const getBarColor = (entry) => {
                    let percentage;
                    if (trackingType === 'percentage') {
                      percentage = entry.score;
                    } else if (trackingType === 'steps_correct' && entry.total_possible) {
                      percentage = (entry.score / entry.total_possible) * 100;
                    } else {
                      return getScoreColor(entry.score);
                    }
                    
                    if (percentage >= 80) return '#5D8D78';
                    if (percentage >= 60) return '#22C55E';
                    if (percentage >= 40) return '#EAB308';
                    if (percentage >= 20) return '#F97316';
                    return '#EF4444';
                  };
                  
                  // Format average score display
                  const formatAverage = () => {
                    if (trackingType === 'percentage') {
                      return `${stats.average_score || 0}%`;
                    } else if (trackingType === 'steps_correct') {
                      return `${stats.average_score || 0}%`;
                    }
                    return `${stats.average_score || 0}/5`;
                  };
                  
                  return (
                    <div 
                      key={goal.goal_id}
                      className="bg-white rounded-2xl p-6 shadow-card hover:shadow-float transition-all"
                      data-testid={`goal-card-${goal.goal_id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-heading font-semibold text-lg text-text-primary">
                            {goal.name}
                          </h3>
                          {goal.description && (
                            <p className="font-body text-sm text-text-muted mt-1">
                              {goal.description}
                            </p>
                          )}
                          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                            trackingType === 'percentage' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {trackingType === 'percentage' ? '% Accuracy' : 'Steps/Problems'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setGoalToDelete(goal);
                            setDeleteGoalOpen(true);
                          }}
                          className="text-text-muted hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Mini Progress Chart */}
                      <div className="bg-secondary/30 rounded-xl p-4 mb-4">
                        <div className="flex items-end justify-between h-20 gap-1">
                          {entries.length > 0 ? (
                            entries.slice(-10).map((entry, i) => (
                              <div
                                key={i}
                                className="flex-1 rounded-t transition-all"
                                style={{
                                  height: getBarHeight(entry),
                                  backgroundColor: getBarColor(entry)
                                }}
                                title={trackingType === 'steps_correct' && entry.total_possible 
                                  ? `${entry.date}: ${entry.score}/${entry.total_possible}`
                                  : `${entry.date}: ${entry.score}%`
                                }
                              />
                            ))
                          ) : (
                            <p className="text-text-muted text-sm w-full text-center self-center">
                              No data yet
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-text-muted">Avg: </span>
                          <span className="font-semibold text-text-primary">
                            {formatAverage()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {stats.trend === 'improving' ? (
                            <span className="text-primary font-semibold">↑ Improving</span>
                          ) : stats.trend === 'stable' ? (
                            <span className="text-accent font-semibold">→ Stable</span>
                          ) : (
                            <span className="text-text-muted">No trend</span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs text-text-muted mt-2">
                        {stats.total_entries || 0} entries • {goal.target_frequency}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Add Goal Dialog */}
      <Dialog open={addGoalOpen} onOpenChange={setAddGoalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add Skill Goal</DialogTitle>
            <DialogDescription className="font-body">
              Create a new skill goal to track for {selectedFolder?.student_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Goal Name</Label>
              <Input
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                placeholder="e.g., Using calming strategies"
                className="mt-2"
                data-testid="goal-name-input"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Description (optional)</Label>
              <Textarea
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="e.g., Student will use deep breathing when feeling overwhelmed"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">How do you want to track progress?</Label>
              <div className="mt-2 space-y-2">
                {TRACKING_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      newGoal.tracking_type === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-secondary hover:border-primary/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tracking_type"
                      value={opt.value}
                      checked={newGoal.tracking_type === opt.value}
                      onChange={(e) => setNewGoal({ ...newGoal, tracking_type: e.target.value })}
                      className="mt-1"
                      data-testid={`tracking-type-${opt.value}`}
                    />
                    <div>
                      <p className="font-semibold text-text-primary">{opt.label}</p>
                      <p className="text-sm text-text-muted">{opt.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-body font-semibold">Tracking Frequency</Label>
              <Select 
                value={newGoal.target_frequency} 
                onValueChange={(v) => setNewGoal({ ...newGoal, target_frequency: v })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGoalOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGoal} 
              disabled={saving}
              className="bg-primary text-white rounded-full"
              data-testid="save-goal-btn"
            >
              {saving ? 'Creating...' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Progress Dialog */}
      <Dialog open={addEntryOpen} onOpenChange={setAddEntryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Record Progress</DialogTitle>
            <DialogDescription className="font-body">
              Record progress for {selectedFolder?.student_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Goal</Label>
              <Select 
                value={newEntry.goal_id} 
                onValueChange={(v) => {
                  const goal = goals.find(g => g.goal_id === v);
                  const trackingType = goal?.tracking_type || 'percentage';
                  setNewEntry({ 
                    ...newEntry, 
                    goal_id: v,
                    score: trackingType === 'percentage' ? 0 : 0,
                    total_possible: 10
                  });
                }}
              >
                <SelectTrigger className="mt-2" data-testid="entry-goal-select">
                  <SelectValue placeholder="Select a goal" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((goal) => (
                    <SelectItem key={goal.goal_id} value={goal.goal_id}>
                      <span>{goal.name}</span>
                      <span className="text-xs text-text-muted ml-2">
                        ({goal.tracking_type === 'steps_correct' ? 'Steps/Problems' : 'Percentage'})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body font-semibold">Date</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                className="mt-2"
                data-testid="entry-date-input"
              />
            </div>
            
            {/* Dynamic scoring based on tracking type */}
            {(() => {
              const selectedGoal = goals.find(g => g.goal_id === newEntry.goal_id);
              const trackingType = selectedGoal?.tracking_type || 'percentage';
              
              if (trackingType === 'percentage') {
                return (
                  <div>
                    <Label className="font-body font-semibold">Percentage Accuracy (0-100%)</Label>
                    <div className="mt-2">
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={newEntry.score}
                          onChange={(e) => setNewEntry({ ...newEntry, score: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                          className="w-24 text-center text-lg font-bold"
                          data-testid="percentage-input"
                        />
                        <span className="text-2xl font-bold text-primary">%</span>
                        <div className="flex-1">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={newEntry.score}
                            onChange={(e) => setNewEntry({ ...newEntry, score: parseInt(e.target.value) })}
                            className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #5D8D78 0%, #5D8D78 ${newEntry.score}%, #E5E7EB ${newEntry.score}%, #E5E7EB 100%)`
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-text-muted mt-2">
                        <span>0%</span>
                        <span className={`font-medium ${newEntry.score >= 80 ? 'text-primary' : newEntry.score >= 50 ? 'text-accent' : 'text-destructive'}`}>
                          {newEntry.score >= 80 ? 'Proficient' : newEntry.score >= 50 ? 'Developing' : 'Needs Support'}
                        </span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // steps_correct tracking
                return (
                  <div>
                    <Label className="font-body font-semibold">Steps/Problems Correct</Label>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-text-muted">Correct</Label>
                        <Input
                          type="number"
                          min="0"
                          max={newEntry.total_possible}
                          value={newEntry.score}
                          onChange={(e) => setNewEntry({ ...newEntry, score: Math.min(newEntry.total_possible, Math.max(0, parseInt(e.target.value) || 0)) })}
                          className="text-center text-lg font-bold"
                          data-testid="steps-correct-input"
                        />
                      </div>
                      <span className="text-2xl font-bold text-text-muted mt-5">out of</span>
                      <div className="flex-1">
                        <Label className="text-xs text-text-muted">Total</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newEntry.total_possible}
                          onChange={(e) => setNewEntry({ ...newEntry, total_possible: Math.max(1, parseInt(e.target.value) || 1) })}
                          className="text-center text-lg font-bold"
                          data-testid="steps-total-input"
                        />
                      </div>
                    </div>
                    <div className="mt-3 bg-secondary/30 rounded-lg p-3 text-center">
                      <span className="text-sm text-text-muted">Accuracy: </span>
                      <span className={`text-lg font-bold ${
                        (newEntry.score / newEntry.total_possible) >= 0.8 ? 'text-primary' : 
                        (newEntry.score / newEntry.total_possible) >= 0.5 ? 'text-accent' : 'text-destructive'
                      }`}>
                        {newEntry.total_possible > 0 ? Math.round((newEntry.score / newEntry.total_possible) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                );
              }
            })()}
            
            <div>
              <Label className="font-body font-semibold">Notes (optional)</Label>
              <Textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="Any observations or context..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEntryOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEntry} 
              disabled={saving}
              className="bg-accent text-white rounded-full"
              data-testid="save-entry-btn"
            >
              {saving ? 'Saving...' : 'Save Progress'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Report Dialog */}
      <Dialog open={emailReportOpen} onOpenChange={setEmailReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Email Progress Report</DialogTitle>
            <DialogDescription className="font-body">
              Send progress report for {selectedFolder?.student_name} to a collaborator
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Recipient Email</Label>
              <Input
                type="email"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                placeholder="parent@example.com"
                className="mt-2"
                data-testid="email-recipient-input"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Recipient Name (optional)</Label>
              <Input
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                placeholder="e.g., Mrs. Johnson"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Personal Message (optional)</Label>
              <Textarea
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                placeholder="Add a personal note..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailReportOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleEmailReport} 
              disabled={saving}
              className="bg-primary text-white rounded-full"
              data-testid="send-email-btn"
            >
              {saving ? 'Sending...' : 'Send Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Goal Confirmation */}
      <AlertDialog open={deleteGoalOpen} onOpenChange={setDeleteGoalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete goal?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will delete "{goalToDelete?.name}" and all associated progress entries. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteGoal}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProgressTracking;
